/**
 * Multi-series trend line over a shared categorical x-axis.
 * Geometry ported from block ① of docs/plans/conversations/09-dashboard-mockup.html.
 *
 * What is load-bearing here:
 *  - A HOLE IS NOT A ZERO: a `null` point closes the current subpath; the line is
 *    broken, never bridged, never drawn at 0.
 *  - Direct end labels (no legend hunting) with LEADER LINES when they collide:
 *    ends sorted by y, nudged forward with MINGAP, clamped backward off the bottom,
 *    then a thin connector at 0.55 opacity from the label to the line end.
 *  - Event markers: 1px vertical rule + small downward flag + label in a reserved
 *    top band, nudged horizontally, clamped at the right edge, each with an 18px
 *    invisible hover hit rect. Never a series colour (an event is not a measure).
 *  - One axis. Crosshair + tooltip listing every series' value at that x.
 *
 * x labels arrive already bucketed by the caller (weekly rollup etc.).
 */

import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import { VIZ, fmtPct, measureText, niceTicks, useMeasuredWidth } from './tokens';
import { EmptyNote, VizLegend } from './ChartCard';
import { useVizTooltip } from './useVizTooltip';

export interface TrendLineSeries {
  key: string;
  label: string;
  color: string;
  points: (number | null)[];
  /**
   * Stroke width, default 2. Only reason to raise it: one series is an AGGREGATE of
   * the others (an average), which is not a category — so it is set apart by weight
   * and an ink colour rather than by being handed a categorical hue it would then
   * steal from a real series.
   */
  width?: number;
}

export interface TrendLineProps {
  labels: string[];
  series: TrendLineSeries[];
  events?: { index: number; label: string }[];
  format?: (v: number) => string;
  yMax?: number;
  height?: number;
  emptyNote?: string;
  unmeasuredNote?: string;
}

const MINGAP = 15;
const BAND = 24; // reserved top band for the event labels

export default function TrendLine({
  labels,
  series,
  events = [],
  format = fmtPct,
  yMax,
  height = 316,
  emptyNote = 'Aucune donnée sur cette sélection.',
  unmeasuredNote = 'aucune mesure',
}: TrendLineProps) {
  const [ref, width] = useMeasuredWidth<HTMLDivElement>(900);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tip = useVizTooltip();
  const [crossI, setCross] = useState<number | null>(null);

  const np = labels.length;
  const hasPoint = series.some((s) => s.points.some((p) => p != null));
  if (!np || !series.length || !hasPoint) return <EmptyNote>{emptyNote}</EmptyNote>;

  // --- end labels first: they set the right margin, so nothing is ever clipped ---
  const lastIdx = (vals: (number | null)[]) => {
    for (let i = vals.length - 1; i >= 0; i--) if (vals[i] != null) return i;
    return null;
  };
  const endTexts = series.map((s) => {
    const i = lastIdx(s.points);
    return i == null ? '' : `${s.label} ${format(s.points[i] as number)}`;
  });
  const endW = Math.max(0, ...endTexts.map((t) => measureText(t, 11.5)));

  const W = Math.max(360, width);
  const H = height;
  const m = { t: events.length ? 14 + BAND : 16, r: endW ? endW + 20 : 12, b: 28, l: 48 };
  const pw = Math.max(40, W - m.l - m.r);
  const ph = Math.max(60, H - m.t - m.b);

  const all = series.flatMap((s) => s.points).filter((v): v is number => v != null);
  // `yMax` is a floor: rounded up to the next nice step so the top gridline is the axis top.
  const ticks = niceTicks(Math.max(yMax ?? 0, 0.0001, ...all), 5);
  const max = ticks[ticks.length - 1] || 1;
  const X = (i: number) => m.l + (i / Math.max(1, np - 1)) * pw;
  const Y = (v: number) => m.t + ph - (v / max) * ph;

  // --- null-breaking subpaths -------------------------------------------------
  const pathOf = (points: (number | null)[]) => {
    let d = '';
    let open = false;
    points.forEach((v, i) => {
      if (v == null) {
        open = false;
        return;
      }
      d += `${open ? 'L' : 'M'}${X(i).toFixed(1)},${Y(v).toFixed(1)}`;
      open = true;
    });
    return d;
  };

  // --- end-label layout: nudge forward, clamp backward, then leader lines -----
  type End = { px: number; py: number; ly: number; color: string; text: string };
  const ends: End[] = [];
  series.forEach((s, j) => {
    const i = lastIdx(s.points);
    if (i == null) return;
    ends.push({ px: X(i), py: Y(s.points[i] as number), ly: 0, color: s.color, text: endTexts[j] });
  });
  ends.sort((a, b) => a.py - b.py);
  ends.forEach((e, i) => {
    e.ly = i ? Math.max(e.py, ends[i - 1].ly + MINGAP) : e.py;
  });
  for (let i = ends.length - 1; i > 0; i--) {
    if (ends[i].ly > m.t + ph) ends[i].ly = m.t + ph;
    if (ends[i].ly - ends[i - 1].ly < MINGAP) ends[i - 1].ly = ends[i].ly - MINGAP;
  }
  const labelX = m.l + pw + 12;

  // --- event label layout: horizontal nudging + right-edge clamp --------------
  const evs = events
    .filter((e) => e.index >= 0 && e.index < np)
    .map((e) => ({ ...e, x: X(e.index), w: measureText(e.label, 11), lx: 0 }))
    .sort((a, b) => a.x - b.x);
  evs.forEach((e, i) => {
    e.lx = i ? Math.max(e.x, evs[i - 1].lx + evs[i - 1].w + 12) : e.x;
    if (e.lx + e.w > m.l + pw) e.lx = m.l + pw - e.w;
  });

  const xTicks = [0, 1, 2, 3]
    .map((j) => Math.round((j / 3) * (np - 1)))
    .filter((v, i, a) => a.indexOf(v) === i);

  const onMove = (ev: React.PointerEvent<SVGRectElement>) => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return;
    const px = ((ev.clientX - box.left) / box.width) * W;
    const i = Math.max(0, Math.min(np - 1, Math.round(((px - m.l) / pw) * (np - 1))));
    setCross(i);
    tip.show(ev, [
      labels[i],
      ...series.map((s) => `${s.label} : ${s.points[i] == null ? unmeasuredNote : format(s.points[i] as number)}`),
      ...evs.filter((e) => e.index === i).map((e) => e.label),
    ]);
  };

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: 'block', overflow: 'visible' }}
        role="img"
        aria-label={`Évolution de ${series.map((s) => s.label).join(', ')}`}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={m.l} x2={m.l + pw} y1={Y(t)} y2={Y(t)} stroke={VIZ.grid} strokeWidth={1} />
            <text
              x={m.l - 8}
              y={Y(t) + 4}
              textAnchor="end"
              fill={VIZ.muted}
              fontSize={11}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {format(t)}
            </text>
          </g>
        ))}
        <line x1={m.l} x2={m.l} y1={m.t} y2={m.t + ph} stroke={VIZ.axis} strokeWidth={1} />

        {xTicks.map((i, j) => (
          <text
            key={i}
            x={X(i)}
            y={H - 8}
            textAnchor={j === 0 ? 'start' : j === xTicks.length - 1 ? 'end' : 'middle'}
            fill={VIZ.muted}
            fontSize={11}
          >
            {labels[i]}
          </text>
        ))}

        {/* event markers — rule + flag + banded label, never a series colour */}
        {evs.map((e) => (
          <g key={`${e.index}-${e.label}`}>
            <line x1={e.x} x2={e.x} y1={m.t - 6} y2={m.t + ph} stroke={VIZ.axis} strokeWidth={1} />
            <path d={`M${e.x},${m.t - 6}l-4,-5h8z`} fill={VIZ.axis} />
            <text x={e.lx} y={m.t - 14} fill={VIZ.muted} fontSize={11}>
              {e.label}
            </text>
            {e.lx !== e.x && (
              <line
                x1={e.lx + 2}
                x2={e.x}
                y1={m.t - 10}
                y2={m.t - 7}
                stroke={VIZ.axis}
                strokeWidth={1}
                opacity={0.6}
              />
            )}
            <rect
              x={e.x - 9}
              y={m.t - BAND}
              width={18}
              height={ph + BAND}
              fill="transparent"
              style={{ cursor: 'help' }}
              onPointerMove={(ev) => {
                ev.stopPropagation();
                tip.show(ev, [labels[e.index], e.label]);
              }}
              onPointerLeave={tip.hide}
            />
          </g>
        ))}

        {series.map((s) => (
          <path
            key={s.key}
            d={pathOf(s.points)}
            fill="none"
            stroke={s.color}
            strokeWidth={s.width ?? 2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {/* end markers: >= 8px diameter with a 2px surface-colour ring */}
        {ends.map((e) => (
          <circle
            key={e.text}
            cx={e.px}
            cy={e.py}
            r={4}
            fill={e.color}
            stroke={VIZ.surface}
            strokeWidth={2}
          />
        ))}

        {/* direct end labels + leader lines */}
        {ends.map((e) => (
          <g key={`lab-${e.text}`}>
            {Math.abs(e.ly - e.py) > 2 && (
              <polyline
                points={`${e.px + 6},${e.py} ${labelX - 6},${e.ly} ${labelX - 2},${e.ly}`}
                fill="none"
                stroke={e.color}
                strokeWidth={1}
                opacity={0.55}
              />
            )}
            <text x={labelX} y={e.ly + 4} fill={VIZ.ink2} fontSize={11.5}>
              {e.text}
            </text>
          </g>
        ))}

        {crossI != null && (
          <line
            x1={X(crossI)}
            x2={X(crossI)}
            y1={m.t}
            y2={m.t + ph}
            stroke={VIZ.axis}
            strokeWidth={1}
          />
        )}
        <rect
          x={m.l}
          y={m.t}
          width={pw}
          height={ph}
          fill="transparent"
          style={{ cursor: 'crosshair' }}
          onPointerMove={onMove}
          onPointerLeave={() => {
            setCross(null);
            tip.hide();
          }}
        />
      </svg>
      {series.length >= 2 && (
        <VizLegend kind="line" items={series.map((s) => ({ label: s.label, color: s.color }))} />
      )}
      {tip.node}
    </Box>
  );
}
